import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';


interface MobileMenuLink {
  label: string;
  href: string;
}

interface MobileMenuProps {
  isDarkMode: boolean;
  links: MobileMenuLink[];
  activeHref?: string;
  ctaHref?: string;
  ctaLabel?: string;
}

export default function MobileMenu({ isDarkMode, links, activeHref, ctaHref, ctaLabel }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full border transition-all cursor-pointer ${
          isDarkMode ? 'border-white/30 text-white hover:bg-white/10' : 'border-black/20 text-black hover:bg-black/5'
        }`}
        aria-label="Open menu"
      >
        <i className="ri-menu-line text-sm"></i>
      </button>

      {createPortal(
        <div className={`fixed inset-0 z-[60] md:hidden ${isOpen ? '' : 'pointer-events-none'}`}>
          <div
            onClick={() => setIsOpen(false)}
            className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
              isOpen ? 'opacity-100' : 'opacity-0'
            }`}
          />

          <div
            className={`absolute top-0 right-0 h-full w-[78%] max-w-xs flex flex-col px-6 py-5 transition-transform duration-300 ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            } ${isDarkMode ? 'bg-[#0a0a0a] border-l border-white/10' : 'bg-white border-l border-black/10'}`}
          >
            <div className="flex items-center justify-between mb-12">
              <Link to="/" className={`text-base font-semibold tracking-tight ${isDarkMode ? 'text-white' : 'text-black'}`}>
                Solvarion LTD
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all cursor-pointer ${
                  isDarkMode ? 'border-white/30 text-white hover:bg-white/10' : 'border-black/20 text-black hover:bg-black/5'
                }`}
                aria-label="Close menu"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>

            <div className="flex flex-col gap-6">
              {links.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`text-base font-medium transition-opacity hover:opacity-70 cursor-pointer ${
                    activeHref === link.href
                      ? isDarkMode
                        ? 'text-white'
                        : 'text-black'
                      : isDarkMode
                      ? 'text-white/70'
                      : 'text-black/70'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {ctaHref && ctaLabel && (
              <a
                href={ctaHref}
                onClick={() => setIsOpen(false)}
                className={`mt-auto inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-medium hover:scale-[1.02] transition-transform duration-300 whitespace-nowrap cursor-pointer ${
                  isDarkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-900'
                }`}
              >
                {ctaLabel}
              </a>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
